/**
 * 家园互动效果层（I 307b，2026-10-03 用户：「比如猫在家里有时候在睡着的时候，你碰碰它或者点柜子的那些效果，我觉得还是要有的」）。
 *
 * 用法（HomeScene 里一行，放在底图 <img className="ps-living-background"> 后面、天色层前面）：
 *   <HomeEffects scene={sceneRef} room={room} reduced={reduced} />
 * 场景里可点的物件带 data-thing="cabinet|door|garden|memento|photo|pet"；宠物框睡着时带 data-asleep="true"（或 data-pose="sleeping"）。
 *
 * 守的规矩：
 * - 纯画面：不改任何状态、不调接口、不写记录；不拦事件（不 preventDefault、不 stopPropagation），气泡、Escape、react() 照旧。
 * - 睡着被碰：起伏一下、耳朵抖一下、冒一串 Zz，不醒、不蹦、不冒心（c84a「睡眠不因装饰点击伪造唤醒」「纯抚摸动画不冒充照顾写入」）。
 * - 醒着被碰：在里面的形象图上朝点的那侧歪个头、或蹭一蹭；外层按钮上 HomeScene 自己的蹦一下和冒心不动（不抢同一个 transform）。
 * - 柜子、屋门、植物、灯这些烘在底图里：从底图裁出那一块的克隆单独动，播完就删。
 * - 装饰小热区 aria-hidden、不进 Tab；层级压在宠物和所有可点物件下面。
 * - 减少动态效果：只淡淡一闪，不冒粒子；灯照样能开关（瞬间，不渐变）。
 * - 键盘：Enter、空格触发的 click 也会走到这里（detail 为 0 时按物件中心算）。
 * - 不出声：项目里还没有家园音效和静音开关，这一层不加音频。
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { HomeRoom } from "@/shared/contracts";
import { plateRectPct } from "../homeStage";
import { DECOR, THING_EFFECT, type BurstKind, type Decor } from "./decor";
import { motionDuration, play, prefersReducedMotion, type MotionName } from "./motion";
import "./effects.css";

type Point = { x: number; y: number };
type Burst = { id: number; kind: BurstKind; x: number; y: number; w: number; h: number };

/** 粒子多久以后从页面上拿掉（最长那一颗的时长＋延迟，留一点余量） */
export const BURST_MS = 1700;

/** 每种粒子画几颗、每颗的偏移／延迟／大小（固定表，不用随机数：同样的点按出来一样，用例也好钉） */
const PARTICLES: Record<BurstKind, Array<{ dx: number; dy: number; delay: number; size: number; rot: number; text?: string }>> = {
  zzz: [
    { dx: 10, dy: -34, delay: 0, size: 13, rot: -8, text: "z" },
    { dx: 22, dy: -58, delay: 220, size: 17, rot: 6, text: "z" },
    { dx: 36, dy: -86, delay: 440, size: 22, rot: -4, text: "Z" },
  ],
  hearts: [],
  sparkles: [
    { dx: -26, dy: -22, delay: 0, size: 10, rot: 0 },
    { dx: 24, dy: -30, delay: 80, size: 13, rot: 20 },
    { dx: -6, dy: -44, delay: 160, size: 8, rot: 45 },
    { dx: 30, dy: 2, delay: 220, size: 9, rot: 10 },
  ],
  leaves: [
    { dx: -18, dy: 26, delay: 0, size: 11, rot: 140 },
    { dx: 14, dy: 38, delay: 120, size: 9, rot: -120 },
    { dx: 30, dy: 18, delay: 260, size: 10, rot: 200 },
  ],
  petals: [
    { dx: -16, dy: -26, delay: 0, size: 8, rot: 90 },
    { dx: 12, dy: -34, delay: 90, size: 7, rot: -60 },
    { dx: 26, dy: -16, delay: 200, size: 8, rot: 150 },
    { dx: -4, dy: -40, delay: 300, size: 6, rot: 30 },
  ],
  glow: [{ dx: 0, dy: 0, delay: 0, size: 0, rot: 0 }],
};

function sceneBox(scene: HTMLElement) {
  return scene.getBoundingClientRect();
}

/** 元素在场景里的位置（像素，相对场景左上角） */
function rectIn(scene: HTMLElement, el: Element) {
  const s = sceneBox(scene);
  const r = el.getBoundingClientRect();
  return { left: r.left - s.left, top: r.top - s.top, width: r.width, height: r.height };
}

/**
 * 从底图裁出一块（和底图同一张图、同一个缩放裁切），放在 host 里同一个位置，返回外框。
 * 做法：外框按那一块的位置和大小、overflow 隐藏；里面放一张和底图一模一样排版的图（同样的 object-fit／object-position），往回挪到对齐原图。
 * 这样不用自己算缩放：浏览器怎么排底图，克隆就怎么排。
 */
export function cloneRegion(scene: HTMLElement, host: HTMLElement, background: HTMLImageElement, rect: { left: number; top: number; width: number; height: number }) {
  const s = sceneBox(scene);
  const b = background.getBoundingClientRect();
  const style = typeof getComputedStyle === "function" ? getComputedStyle(background) : null;
  const wrap = document.createElement("div");
  wrap.className = "ps-fx-clone";
  wrap.setAttribute("aria-hidden", "true");
  Object.assign(wrap.style, { left: `${rect.left}px`, top: `${rect.top}px`, width: `${rect.width}px`, height: `${rect.height}px` });
  const img = document.createElement("img");
  img.alt = "";
  img.draggable = false;
  img.src = background.currentSrc || background.src;
  Object.assign(img.style, {
    left: `${b.left - s.left - rect.left}px`,
    top: `${b.top - s.top - rect.top}px`,
    width: `${b.width}px`,
    height: `${b.height}px`,
    objectFit: style?.objectFit || "cover",
    objectPosition: background.style.objectPosition || style?.objectPosition || "50% 0",
  });
  wrap.appendChild(img);
  host.appendChild(wrap);
  return wrap;
}

function isAsleep(pet: HTMLElement): boolean {
  return pet.dataset.asleep === "true" || pet.dataset.pose === "sleeping" || pet.classList.contains("is-asleep");
}

/** 宠物框里真正画出 TA 的那张图（外层按钮上 HomeScene 自己有蹦一下的 transform，这一层动里面，不抢） */
function petVisual(pet: HTMLElement): Element {
  return pet.querySelector(".ps-pet-figure img, .ps-living-pet__identity, img") ?? pet;
}

export function HomeEffects({ scene, room, reduced: reducedProp }: { scene: RefObject<HTMLElement | null>; room: HomeRoom; reduced?: boolean }) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [bursts, setBursts] = useState<Burst[]>([]);
  const [lampsOn, setLampsOn] = useState<ReadonlySet<string>>(() => new Set());
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const nextId = useRef(1);
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const pokes = useRef(0);
  const reducedRef = useRef(false);
  reducedRef.current = reducedProp ?? prefersReducedMotion();

  const later = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timers.current.delete(t);
      fn();
    }, ms);
    timers.current.add(t);
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const t of pending) clearTimeout(t);
      pending.clear();
    };
  }, []);

  useEffect(() => setPortalTarget(scene.current), [scene]);

  // 换房间：上一间的灯不带过来
  useEffect(() => setLampsOn(new Set()), [room]);

  // 换房间：上一间裁出来的克隆块（院门咚咚两下）当场停掉拿走、粒子清空，不叠在这一间的底图上（用户 10-09「门和屋内重叠」，HOME-DOOR-FIX-01）。
  // layout effect：和换底图同一次提交、绘制之前，屋内第一帧就没有院门。点屋门的按压底色松手就结束，不受影响。
  useLayoutEffect(() => {
    hostRef.current?.querySelectorAll<HTMLElement>(".ps-fx-clone").forEach((clone) => {
      clone.getAnimations?.().forEach((animation) => animation.cancel());
      clone.remove();
    });
    setBursts((list) => (list.length ? [] : list));
  }, [room]);

  const burst = useCallback(
    (kind: BurstKind | null, at: { left: number; top: number; width: number; height: number }, anchor: "top" | "center" = "center") => {
      if (!kind || reducedRef.current) return;
      const id = nextId.current++;
      const x = at.left + at.width / 2;
      const y = anchor === "top" ? at.top + at.height * 0.18 : at.top + at.height / 2;
      setBursts((list) => [...list, { id, kind, x, y, w: at.width, h: at.height }]);
      later(() => setBursts((list) => list.filter((b) => b.id !== id)), BURST_MS);
    },
    [later],
  );

  /** 烘在底图里的那一块：裁一个克隆出来动，播完就删 */
  const moveBaked = useCallback(
    (rect: { left: number; top: number; width: number; height: number }, motion: MotionName) => {
      const sceneEl = scene.current;
      const host = hostRef.current;
      const background = sceneEl?.querySelector<HTMLImageElement>("img.ps-living-background");
      if (!sceneEl || !host || !background) return;
      const clone = cloneRegion(sceneEl, host, background, rect);
      const animation = play(clone, motion, reducedRef.current);
      const done = () => clone.remove();
      if (animation) {
        animation.addEventListener?.("finish", done);
        animation.addEventListener?.("cancel", done);
      }
      // 没有 WAAPI，或者事件没来：到时间也删掉，不留在页面上
      later(done, motionDuration(motion, reducedRef.current) + 120);
    },
    [scene, later],
  );

  // 场景上的事件委托：只看点到的是不是 data-thing，看完就放行（不拦、不改）
  useEffect(() => {
    const sceneEl = scene.current;
    if (!sceneEl) return;
    const onClick = (event: MouseEvent) => {
      const target = (event.target as Element | null)?.closest<HTMLElement>("[data-thing]");
      if (!target || !sceneEl.contains(target)) return;
      const thing = target.dataset.thing ?? "";
      const reduced = reducedRef.current;
      const rect = rectIn(sceneEl, target);
      if (thing === "pet") {
        const visual = petVisual(target);
        if (isAsleep(target)) {
          // 睡着：起伏一下，耳朵再抖一下，冒 Zz；不醒、不蹦、不冒心
          play(visual, "stir", reduced);
          if (!reduced) later(() => play(visual, "earFlick", false), motionDuration("stir", false) - 200);
          // 有睡姿时头顶本来就挂着一串 z（HomeScene 的 .ps-living-pet__zzz）：只起伏，不再冒一串，免得重（6c2b 10-03）
          if (!target.querySelector(".ps-living-pet__zzz")) burst("zzz", rect, "top");
          return;
        }
        // 醒着：朝点的那一侧歪个头；连着点就蹭一蹭（隔一下换一个，不老是同一个动作）
        pokes.current += 1;
        const point: Point =
          event.detail === 0 ? { x: rect.left + rect.width / 2, y: rect.top } : { x: event.clientX - sceneBox(sceneEl).left, y: event.clientY - sceneBox(sceneEl).top };
        const motion: MotionName = pokes.current % 2 === 0 ? "nuzzle" : point.x < rect.left + rect.width / 2 ? "tiltLeft" : "tiltRight";
        play(visual, motion, reduced);
        return;
      }
      const effect = THING_EFFECT[thing];
      if (!effect) return;
      if (effect.baked) moveBaked(rect, effect.motion);
      else play(target.querySelector("img") ?? target.firstElementChild ?? target, effect.motion, reduced);
      burst(effect.burst, rect);
    };
    sceneEl.addEventListener("click", onClick);
    return () => sceneEl.removeEventListener("click", onClick);
  }, [scene, burst, later, moveBaked]);

  const decor = DECOR[room];
  const boxes = useMemo(
    () =>
      decor.map((d) => {
        const pct = plateRectPct(room, d);
        return { d, style: { left: `${pct.left}%`, top: `${pct.top}%`, width: `${pct.width}%`, height: `${pct.height}%` } as CSSProperties };
      }),
    [decor, room],
  );

  const onDecor = (d: Decor, el: HTMLElement) => {
    const sceneEl = scene.current;
    if (!sceneEl) return;
    const rect = rectIn(sceneEl, el);
    moveBaked(rect, d.motion);
    if (d.lamp) {
      setLampsOn((current) => {
        const next = new Set(current);
        if (next.has(d.key)) next.delete(d.key);
        else next.add(d.key);
        return next;
      });
    }
    burst(d.burst, rect, d.burst === "glow" ? "center" : "top");
  };

  return (
    <>
      <div ref={hostRef} className="ps-fx-host" aria-hidden="true" data-testid="home-fx-host" />
      {boxes.map(({ d, style }) => (
        <span
          key={`${room}:${d.key}`}
          className={`ps-fx-decor${d.lamp ? " is-lamp" : ""}${lampsOn.has(d.key) ? " is-on" : ""}`}
          style={style}
          aria-hidden="true"
          data-decor={d.key}
          data-testid="home-fx-decor"
          onClick={(event) => onDecor(d, event.currentTarget)}
        >
          {d.lamp ? <span className="ps-fx-lamp-glow" data-testid="home-fx-lamp" data-on={lampsOn.has(d.key) ? "true" : "false"} /> : null}
        </span>
      ))}
      {portalTarget
        ? createPortal(
            <div className="ps-fx-bursts" aria-hidden="true" data-testid="home-fx-bursts">
              {bursts.map((b) => (
                <span
                  key={b.id}
                  className={`ps-fx-burst is-${b.kind}`}
                  data-kind={b.kind}
                  style={{ left: `${b.x}px`, top: `${b.y}px`, "--fx-w": `${b.w}px`, "--fx-h": `${b.h}px` } as CSSProperties}
                >
                  {PARTICLES[b.kind].map((p, i) => (
                    <i
                      key={i}
                      className="ps-fx-p"
                      style={{ "--dx": `${p.dx}px`, "--dy": `${p.dy}px`, "--delay": `${p.delay}ms`, "--size": `${p.size}px`, "--rot": `${p.rot}deg` } as CSSProperties}
                    >
                      {p.text ?? null}
                    </i>
                  ))}
                </span>
              ))}
            </div>,
            portalTarget,
          )
        : null}
    </>
  );
}
